
// This file is automatically generated. Do not edit it directly.
import { createClient } from '@supabase/supabase-js';
import type { Database } from './types';

// Use environment variables for security - these should be set in production
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || "https://fovfxwykkybgkmiuovrh.supabase.co";
const SUPABASE_PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY || "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImZvdmZ4d3lra3liZ2ttaXVvdnJoIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTAxODE3MDQsImV4cCI6MjA2NTc1NzcwNH0.jWDJdPTsa4YEY7OT__zsdvR7M78PDfa66U2Jcx22PU0";

// Import the supabase client like this:
// import { supabase } from "@/integrations/supabase/client";

export const supabase = createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
  auth: {
    // Use localStorage for session storage (standard approach)
    storage: localStorage,
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    debug: false // Disable debug mode to reduce console noise
  },
  global: {
    headers: {
      'x-client-info': 'supabase-js-web'
    }
  },
  realtime: {
    params: {
      eventsPerSecond: 10
    },
    timeout: 20000,
    heartbeatIntervalMs: 30000
  }
});

// Add error handling for auth state changes
supabase.auth.onAuthStateChange((event, session) => {
  console.log('Auth state changed:', event, session?.user?.id);
  
  // Handle token refresh errors
  if (event === 'TOKEN_REFRESHED' && !session) {
    console.warn('Token refresh failed, clearing session');
    supabase.auth.signOut();
  }
  
  // Handle sign out events
  if (event === 'SIGNED_OUT') {
    console.log('User signed out, clearing session');
    // Clear any client-side session data
    sessionStorage.clear();
  }
});

// Add connection test function for debugging
export const testSupabaseConnection = async () => {
  try {
    console.log('Testing Supabase connection...');
    const { data, error } = await supabase.from('profiles').select('count').limit(1);
    if (error) {
      console.error('Supabase connection test failed:', error);
      
      // Handle specific auth errors
      if (error.message.includes('invalid claim') || error.message.includes('bad_jwt')) {
        console.log('Auth token invalid, attempting to refresh session...');
        const { error: refreshError } = await supabase.auth.refreshSession();
        if (refreshError) {
          console.error('Session refresh failed:', refreshError);
          // Clear invalid session
          await supabase.auth.signOut();
        }
      }
      
      return false;
    }
    console.log('Supabase connection test successful');
    return true;
  } catch (err) {
    console.error('Supabase connection test exception:', err);
    return false;
  }
};

// Add helper for checking realtime connection
export const testRealtimeConnection = () => {
  return new Promise((resolve) => {
    const channel = supabase.channel('connection-test');
    
    const timeout = setTimeout(() => {
      console.log('Realtime connection test timed out');
      supabase.removeChannel(channel);
      resolve(false);
    }, 10000);
    
    channel.subscribe((status, err) => {
      if (status === 'SUBSCRIBED') {
        console.log('Realtime connection successful');
        clearTimeout(timeout);
        supabase.removeChannel(channel);
        resolve(true);
      } else if (status === 'CHANNEL_ERROR') {
        console.error('Realtime connection failed:', err);
        clearTimeout(timeout);
        supabase.removeChannel(channel);
        resolve(false);
      }
    });
  });
};

// Add auth state monitoring for debugging
export const monitorAuthState = () => {
  return supabase.auth.onAuthStateChange((event, session) => {
    console.log('Auth state changed:', event, session?.user?.id);
  });
};

// Add helper to handle auth errors gracefully
export const handleAuthError = async (error: any) => {
  if (error?.message?.includes('invalid claim') || 
      error?.message?.includes('bad_jwt') ||
      error?.message?.includes('missing sub claim')) {
    console.warn('Auth error detected, clearing session:', error.message);
    await supabase.auth.signOut();
    return true; // Indicates error was handled
  }
  return false; // Error was not an auth error
};
